import {
    Button,
    Divider,
    Flex,
    MultiSelect,
    Pill,
    Popover,
    Stack,
    Switch,
    Text,
    TextInput
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
    CloseOutlined,
    FilterListOutlined,
    PersonAddOutlined,
    SearchOutlined
} from "@mui/icons-material";
import { SvgIcon } from "@mui/material";
import {
    useEntityListQuery,
    useGetLocationListQuery,
    UserRoleEnum
} from "@therapprove/common";
import { FC, useEffect, useRef } from "react";
import { TeamFilters } from "./useTeam";

type TeamHeaderProps = {
    search: string;
    setSearch: (search: string) => void;
    filters: TeamFilters;
    setFilters: (filters: TeamFilters) => void;
    onAddNew: () => void;
};

// Constants
const ROLE_LABELS: Record<UserRoleEnum, string> = {
    [UserRoleEnum.OrganizationOwner]: "Owner",
    [UserRoleEnum.OrganizationAdmin]: "Admin",
    [UserRoleEnum.CaseManager]: "Coordinator",
    [UserRoleEnum.Provider]: "Provider"
};

const ROLE_OPTIONS = Object.entries(ROLE_LABELS).map(([value, label]) => ({
    value,
    label
}));

// Section label style
const sectionLabelStyle = {
    fontSize: "0.7rem",
    fontWeight: 600,
    color: "var(--mantine-color-gray-6)",
    textTransform: "uppercase" as const,
    padding: "0.25rem 0.5rem 0.1rem"
};

const defaultFilters: TeamFilters = {
    roleNames: [],
    isActive: undefined,
    locationIds: []
};

export const TeamHeader: FC<TeamHeaderProps> = ({
    search,
    setSearch,
    filters,
    setFilters,
    onAddNew
}) => {
    // Queries
    const [{ data: locationData }] = useGetLocationListQuery();
    const locationOptions =
        locationData?.locations?.locationsList
            ?.filter(Boolean)
            .map((l) => ({ value: l!.value, label: l!.label })) ?? [];

    // Data
    const hasActiveFilters =
        filters.roleNames.length > 0 ||
        filters.isActive !== undefined ||
        filters.locationIds.length > 0;

    // State
    const [opened, { toggle, close }] = useDisclosure(false);
    const popoverRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    // Handlers
    const clearFilters = () => setFilters(defaultFilters);

    // Manual click-outside handler
    useEffect(() => {
        if (!opened) return;

        const handleClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            if (!target) return;
            if (buttonRef.current?.contains(target)) return;
            if (popoverRef.current?.contains(target)) return;

            const nestedPopoverDropdown = target.closest(
                ".mantine-Popover-dropdown"
            );
            if (
                nestedPopoverDropdown &&
                nestedPopoverDropdown !== popoverRef.current
            )
                return;

            close();
        };

        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [opened, close]);

    // Active filter pills
    const activePills: { key: string; value: string; label: string }[] = [];

    for (const role of filters.roleNames) {
        activePills.push({
            key: "roleNames",
            value: role,
            label: ROLE_LABELS[role as UserRoleEnum] ?? role
        });
    }
    for (const id of filters.locationIds) {
        const label = locationOptions.find((l) => l.value === id)?.label ?? id;
        activePills.push({ key: "locationIds", value: id, label });
    }
    if (filters.isActive !== undefined) {
        activePills.push({
            key: "isActive",
            value: String(filters.isActive),
            label: "Show inactive"
        });
    }

    const removePill = (key: string, value: string) => {
        if (key === "roleNames") {
            setFilters({
                ...filters,
                roleNames: filters.roleNames.filter((r) => r !== value)
            });
        } else if (key === "locationIds") {
            setFilters({
                ...filters,
                locationIds: filters.locationIds.filter((id) => id !== value)
            });
        } else if (key === "isActive") {
            setFilters({ ...filters, isActive: undefined });
        }
    };

    return (
        <Stack gap="xs">
            {/* Search, filters, and add new */}
            <Flex justify="space-between" align="center">
                <Flex flex={1} gap="sm" align="center">
                    <TextInput
                        placeholder="Search team..."
                        leftSection={
                            <SvgIcon component={SearchOutlined} />
                        }
                        value={search}
                        onChange={(e) => setSearch(e.currentTarget.value)}
                        rightSection={
                            search ? (
                                <SvgIcon
                                    component={CloseOutlined}
                                    sx={{
                                        width: "0.9rem",
                                        height: "0.9rem",
                                        cursor: "pointer"
                                    }}
                                    onClick={() => setSearch("")}
                                />
                            ) : null
                        }
                    />
                    <Popover
                        opened={opened}
                        position="bottom-start"
                        shadow="md"
                        trapFocus={false}
                        closeOnClickOutside={false}
                    >
                        <Popover.Target>
                            <Button
                                ref={buttonRef}
                                variant="default"
                                leftSection={
                                    <SvgIcon component={FilterListOutlined} />
                                }
                                onClick={toggle}
                            >
                                Filters
                            </Button>
                        </Popover.Target>
                        <Popover.Dropdown
                            ref={popoverRef}
                            style={{
                                width: "18rem",
                                maxHeight: "20rem",
                                overflowY: "auto",
                                padding: "0.5rem 0"
                            }}
                        >
                            {/* Role */}
                            <Text style={sectionLabelStyle}>Role</Text>
                            <div style={{ padding: "0 0.5rem 0.25rem" }}>
                                <MultiSelect
                                    variant="pill"
                                    placeholder="Select roles"
                                    data={ROLE_OPTIONS}
                                    value={filters.roleNames}
                                    onChange={(value) =>
                                        setFilters({
                                            ...filters,
                                            roleNames:
                                                value as UserRoleEnum[]
                                        })
                                    }
                                    comboboxProps={{ withinPortal: true }}
                                    clearable
                                    size="xs"
                                />
                            </div>

                            {/* Location */}
                            <Divider my={4} />
                            <Text style={sectionLabelStyle}>Location</Text>
                            <div style={{ padding: "0 0.5rem 0.25rem" }}>
                                <MultiSelect
                                    variant="pill"
                                    placeholder="Select locations"
                                    data={locationOptions}
                                    value={filters.locationIds}
                                    onChange={(value) =>
                                        setFilters({
                                            ...filters,
                                            locationIds: value
                                        })
                                    }
                                    comboboxProps={{ withinPortal: true }}
                                    searchable
                                    clearable
                                    size="xs"
                                />
                            </div>

                            {/* Show inactive */}
                            <Divider my={4} />
                            <div style={{ padding: "0.25rem 0.5rem" }}>
                                <Switch
                                    checked={filters.isActive === false}
                                    onChange={(e) =>
                                        setFilters({
                                            ...filters,
                                            isActive: e.currentTarget.checked
                                                ? false
                                                : undefined
                                        })
                                    }
                                    label="Show inactive"
                                    size="sm"
                                />
                            </div>

                            {/* Clear all */}
                            {hasActiveFilters && (
                                <>
                                    <Divider my={4} />
                                    <Button
                                        variant="subtle"
                                        size="compact-xs"
                                        c="dimmed"
                                        fullWidth
                                        onClick={clearFilters}
                                    >
                                        Clear filters
                                    </Button>
                                </>
                            )}
                        </Popover.Dropdown>
                    </Popover>
                </Flex>
                <Flex flex={1} justify="flex-end">
                    <Button
                        leftSection={<SvgIcon component={PersonAddOutlined} />}
                        onClick={onAddNew}
                    >
                        Add New
                    </Button>
                </Flex>
            </Flex>

            {/* Active filter pills */}
            {hasActiveFilters && (
                <Flex gap={6} align="center" wrap="wrap">
                    <Pill
                        size="xs"
                        style={{
                            cursor: "pointer",
                            backgroundColor:
                                "var(--mantine-color-secondary-5)",
                            color: "white"
                        }}
                        onClick={clearFilters}
                    >
                        Clear all
                    </Pill>
                    {activePills.map((pill) => (
                        <Pill
                            key={`${pill.key}-${pill.value}`}
                            size="xs"
                            withRemoveButton
                            onRemove={() => removePill(pill.key, pill.value)}
                        >
                            {pill.label}
                        </Pill>
                    ))}
                </Flex>
            )}
        </Stack>
    );
};
